import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shield, EyeOff } from "lucide-react";

/**
 * Aggregate diversity view for a job (recruiter-only).
 * Follows min-N suppression from the backend: if respondents < MIN_N
 * the section renders a privacy notice instead of counts.
 */
export const DiversityPanel = ({ jobId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    api.get(`/stats/diversity/${jobId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return <div className="text-slate-500 text-sm py-6 text-center">Loading…</div>;
  if (!data) return null;

  if (data.applicants === 0) {
    return (
      <PrivacyNote text="No applicants yet — nothing to aggregate." />
    );
  }

  return (
    <div data-testid="diversity-panel" className="space-y-4">
      <div className="flex items-start gap-2 text-xs text-slate-500 border border-white/5 rounded-lg p-3 bg-[#0B0F19]/60">
        <Shield className="w-4 h-4 text-[#06B6D4] shrink-0 mt-0.5" />
        <div>
          <div className="text-slate-300 font-medium mb-1">Aggregates only — never individual candidates.</div>
          Counts under {data.min_n} respondents are suppressed to protect anonymity.
          Any bucket with fewer than 3 people is rolled into <em>hidden</em>.
          All fields are self-reported and voluntary.
        </div>
      </div>

      <DiversityCategory title="Gender" bucket={data.gender} />
      <DiversityCategory title="Ethnicity" bucket={data.ethnicity} />
      <DiversityCategory title="Veteran status" bucket={data.veteran_status} />
      <DiversityCategory title="Disability status" bucket={data.disability_status} />
    </div>
  );
};

const DiversityCategory = ({ title, bucket }) => {
  if (!bucket) return null;
  if (bucket.suppressed) {
    return (
      <div className="border border-white/10 rounded-lg p-4">
        <div className="mono-label mb-1">{title}</div>
        <div className="text-slate-500 text-xs flex items-center gap-1.5">
          <EyeOff className="w-3 h-3" />
          Hidden — only {bucket.total} respondent{bucket.total === 1 ? "" : "s"} (need {5}+).
        </div>
      </div>
    );
  }
  const total = bucket.total || 1;
  const entries = Object.entries(bucket.counts || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="border border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="mono-label">{title}</div>
        <div className="text-xs text-slate-500">{bucket.respondents} respondents</div>
      </div>
      <div className="space-y-2">
        {entries.map(([key, count]) => {
          const pct = Math.round((count / total) * 100);
          const isHidden = key === "<hidden>";
          return (
            <div key={key}>
              <div className="flex justify-between text-xs mb-1">
                <span className={`${isHidden ? "text-slate-500 italic" : "text-slate-300"}`}>
                  {isHidden ? "Hidden (small buckets)" : key}
                </span>
                <span className="text-slate-400 font-mono">
                  {count} · {pct}%
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: isHidden ? "#475569" : "#06B6D4",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PrivacyNote = ({ text }) => (
  <div className="text-center py-8 text-slate-500 text-sm">
    <Shield className="w-5 h-5 mx-auto mb-2 text-slate-600" />
    {text}
  </div>
);
